import {ErrorInfo} from 'react'
import * as Sentry from '@sentry/react'
import {IErrorBoundaryStore} from '@/components/containers/ErrorBoundary/IErrorBoundaryStore'
import {ErrorType} from '@/components/containers/ErrorBoundary/types'
import {getErrorTypeLabel} from '@/components/containers/ErrorBoundary/utils'
import {AuthStoreInterface, AuthStoreSymbol} from '@/stores'
import {inject, injectable} from 'inversify'
import {makeAutoObservable} from 'mobx'
import {error} from 'dna-common'
import {userHash} from '@/services/storage'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'

@injectable()
export class ErrorBoundaryStore implements IErrorBoundaryStore {
    private _authStore: AuthStoreInterface

    constructor(
        @inject(AuthStoreSymbol) authStore: AuthStoreInterface
    ) {
        this._authStore = authStore
        makeAutoObservable(this)
    }

    // Function to determine is the error is connected with loading outdated scripts (js ans css)
    // If so, send message to Sentry and reload the page to get the latest version of the scripts
    private processNotAuthError = (caughtError: Error, errorInfo: ErrorInfo, errorType: ErrorType) => {
        captureSentryError({
            name: `ErrorBoundary ${getErrorTypeLabel(errorType)}`,
            description: caughtError.message || 'Unknown error caught in ErrorBoundary',
            fingerprint: ['error-boundary', errorType],
            extra: {
                errorInfo: errorInfo?.componentStack,
                errorType,
                pathname: window.location.pathname
            },
            error: caughtError
        }).then(() => {
            if (errorType === 'LoadChunk') {
                window.location.reload()
            }
        }).catch(() => {
            if (errorType === 'LoadChunk') {
                window.location.reload()
            }
        })
    }

    handleCaughtError(err: Error, errorInfo: React.ErrorInfo, errorType: ErrorType): void {
        error(err, errorInfo, location.pathname)

        if (errorType !== 'AuthError') {
            this.processNotAuthError(err, errorInfo, errorType)
            return
        }

        // Temporary functions. TODO remove later
        // Function to capture localStorage content by using merchant-portal userHash
        const getLocalStorageContent = (): Record<string, any> => {
            const localStorageData: Record<string, any> = {}
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key && key.startsWith(userHash)) {
                    try {
                        localStorageData[key] = JSON.parse(localStorage.getItem(key) || 'null')
                    } catch {
                        localStorageData[key] = localStorage.getItem(key) // Fallback for non-JSON values
                    }
                }
            }
            return localStorageData
        }

        // Function to capture the AuthStore instance state
        const getAuthStoreContent = (authStoreInstance: AuthStoreInterface): Record<string, any> => {
            const authStoreData: Record<string, any> = {}

            // check if authStoreInstance is an object and exists
            if (!authStoreInstance || typeof authStoreInstance !== 'object') {
                return authStoreData
            }

            // get only fields that are not functions
            for (const key in authStoreInstance) {
                if (authStoreInstance.hasOwnProperty(key) && typeof authStoreInstance[key] !== 'function') {
                    try {
                        authStoreData[key] = JSON.stringify(authStoreInstance[key]) // Convert each property to JSON
                    } catch {
                        authStoreData[key] = 'Unable to stringify' // Handle circular references or errors
                    }
                }
            }

            return authStoreData
        }

        // Combine and send data to Sentry
        const sendDataToSentry = (authStoreInstance: any) => {
            const localStorageContent = getLocalStorageContent()
            const authStoreContent = getAuthStoreContent(authStoreInstance)

            const combinedData = {
                localStorage: localStorageContent,
                authStore: authStoreContent,
                error: err,
                errorInfo
            }

            captureSentryError({
                name: 'ErrorBoundary AuthError',
                description: 'Auth error caught in Error Boundary',
                fingerprint: ['error-boundary-auth-error'],
                extra: combinedData,
                error: err
            })

        }

        if (this._authStore) {
            console.log('Sending data to Sentry...')
            sendDataToSentry(this._authStore)

            // clear auth store and local storage
            this._authStore.clearStore()
            this._authStore.clearStorage()
        }
    }

    onGoHomeClick = (shouldClearToken: boolean) => {
        //clear auth store and local storage
        // navigate to the root url with reload
        if (this._authStore && shouldClearToken) {
            this._authStore.clearStore()
            this._authStore.clearStorage()
        }
        window.location.href = '/'
    }
}
